/**
 * MerchantAccessCard — invite the merchant to their own panel (B4).
 *
 * Creates a personal invite link bound to an email (7-day signed token).
 * MVP delivery is inviter-sent: copy the link, or "Enviar por mail" opens the
 * inviter's mail client prefilled (no SES infrastructure yet). The invited
 * person signs in with that mailbox's Google account and lands on /merchant.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, Button, Title, Text } from '@nimbus-ds/components'
import { listInvites, createInvite, revokeInvite, type StoreInvite } from '@/api/invites'

const STATUS_LABEL: Record<StoreInvite['status'], string> = {
  pending: 'Pendiente',
  accepted: 'Aceptada',
  revoked: 'Revocada',
}

export function MerchantAccessCard({ storeId }: { storeId: string }) {
  const [invites, setInvites] = useState<StoreInvite[]>([])
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ email: string; url: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const refresh = useCallback(() => {
    listInvites(storeId)
      .then(setInvites)
      .catch(() => {})
  }, [storeId])

  useEffect(refresh, [refresh])

  const handleCreate = async () => {
    const target = email.trim().toLowerCase()
    if (!target) return
    setBusy(true)
    setError(null)
    setCreated(null)
    setCopied(false)
    try {
      const { invite_url } = await createInvite(storeId, target)
      setCreated({ email: target, url: invite_url })
      setEmail('')
      refresh()
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'No se pudo crear la invitación.')
    } finally {
      setBusy(false)
    }
  }

  const handleCopy = async () => {
    if (!created) return
    try {
      await navigator.clipboard.writeText(created.url)
      setCopied(true)
    } catch {
      /* clipboard unavailable — the link is visible for manual copy */
    }
  }

  const mailtoHref = created
    ? `mailto:${created.email}?subject=${encodeURIComponent('Acceso a tu panel de tienda')}&body=${encodeURIComponent(
        `Hola,\n\nTe invitamos a tu panel: métricas de tu tienda y pruebas para mejorarla.\n\nEntrá con tu cuenta de Google desde este enlace (vence en 7 días):\n${created.url}\n\nSaludos`
      )}`
    : undefined

  return (
    <Card>
      <Card.Header>
        <Title as="h4">Acceso del comerciante</Title>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          <Text fontSize="caption">
            Invitá al comerciante a su panel. La invitación es personal (solo funciona con la
            cuenta de Google de ese correo) y vence en 7 días.
          </Text>

          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@delcomerciante.com"
              className="flex-1 rounded-md border px-3 py-2 text-sm"
            />
            <Button appearance="primary" onClick={handleCreate} disabled={busy || !email.trim()}>
              {busy ? 'Creando…' : 'Invitar'}
            </Button>
          </div>
          {error && <Text fontSize="caption">{error}</Text>}

          {created && (
            <div className="space-y-2 rounded-md border bg-slate-50 p-3">
              <Text fontSize="caption">
                Invitación para <strong>{created.email}</strong> — el enlace se muestra una sola
                vez:
              </Text>
              <code className="block overflow-x-auto rounded bg-white p-2 text-xs">{created.url}</code>
              <div className="flex gap-2">
                <Button appearance="neutral" onClick={handleCopy}>
                  {copied ? '✓ Copiado' : 'Copiar enlace'}
                </Button>
                <a href={mailtoHref}>
                  <Button appearance="neutral">Enviar por mail</Button>
                </a>
              </div>
            </div>
          )}

          {invites.length > 0 && (
            <div className="space-y-1">
              {invites.map((inv) => (
                <div key={inv.invite_id} className="flex items-center justify-between gap-2 border-b py-1.5 last:border-b-0">
                  <div className="min-w-0">
                    <Text fontSize="caption" fontWeight="medium">{inv.email}</Text>
                    <Text fontSize="caption"> {STATUS_LABEL[inv.status]}</Text>
                  </div>
                  {inv.status === 'pending' && (
                    <button
                      className="text-xs text-red-500 hover:underline"
                      onClick={() => revokeInvite(storeId, inv.invite_id).then(refresh)}
                    >
                      Revocar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}
